import { useMemo, useLayoutEffect, useEffect, useState } from "react";
import { CookbookContextProvider, useCookbookContext } from "@creditornot/cb-context";
import { DATA_ATTR_THEME, DATA_ATTR_HEADING_FONT, resolveHeadingFontByLocale } from "@creditornot/cb-utils";
import { jsx } from "react/jsx-runtime";
import "./style.css";
const useIsomorphicLayoutEffect = typeof window === "object" ? useLayoutEffect : useEffect;
const CookbookProvider = ({
  children,
  direction = "ltr",
  locale,
  themeMode = "light"
}) => {
  useIsomorphicLayoutEffect(() => {
    document.documentElement.setAttribute(DATA_ATTR_THEME, themeMode);
  }, [themeMode]);
  useIsomorphicLayoutEffect(() => {
    if (locale) {
      document.documentElement.setAttribute("lang", locale);
    }
    document.documentElement.setAttribute(DATA_ATTR_HEADING_FONT, resolveHeadingFontByLocale(locale ?? "en"));
  }, [locale]);
  useIsomorphicLayoutEffect(() => {
    document.documentElement.setAttribute("dir", direction);
  }, [direction]);
  const contextValue = useMemo(() => ({
    direction,
    themeMode
  }), [direction, themeMode]);
  return /* @__PURE__ */ jsx(CookbookContextProvider, {
    value: contextValue,
    children
  });
};
const isBrowser = typeof window !== "undefined";
const themeObserverCallbacks = [];
let themeObserver;
const useThemeMode = () => {
  const cookbookContext = useCookbookContext();
  if (cookbookContext) {
    return cookbookContext.themeMode;
  }
  if (!isBrowser) {
    return "light";
  }
  const [value, setValue] = useState(document.documentElement.getAttribute(DATA_ATTR_THEME) ?? "light");
  useEffect(() => {
    const updateAttribute = (element) => {
      const attr = element.getAttribute(DATA_ATTR_THEME);
      if (attr) {
        setValue(attr);
      }
    };
    if (!themeObserver) {
      themeObserver = new MutationObserver((mutations) => {
        themeObserverCallbacks.forEach((callback) => callback(mutations[0].target));
      });
      themeObserver.observe(document.documentElement, {
        attributeFilter: [DATA_ATTR_THEME]
      });
    }
    themeObserverCallbacks.push(updateAttribute);
    updateAttribute(document.documentElement);
    return () => {
      const index = themeObserverCallbacks.indexOf(updateAttribute);
      if (index > -1) {
        themeObserverCallbacks.splice(index, 1);
      }
    };
  }, []);
  return value;
};
const directionObserverCallbacks = [];
let directionObserver;
const useDirection = () => {
  const cookbookContext = useCookbookContext();
  if (cookbookContext) {
    return cookbookContext.direction;
  }
  if (!isBrowser) {
    return "ltr";
  }
  const [value, setValue] = useState(document.documentElement.getAttribute("dir") ?? "ltr");
  useEffect(() => {
    const updateAttribute = (element) => {
      const attr = element.getAttribute("dir");
      if (attr) {
        setValue(attr);
      }
    };
    if (!themeObserver) {
      directionObserver = new MutationObserver((mutations) => {
        directionObserverCallbacks.forEach((callback) => callback(mutations[0].target));
      });
      directionObserver.observe(document.documentElement, {
        attributeFilter: ["dir"]
      });
    }
    directionObserverCallbacks.push(updateAttribute);
    updateAttribute(document.documentElement);
    return () => {
      const index = directionObserverCallbacks.indexOf(updateAttribute);
      if (index > -1) {
        directionObserverCallbacks.splice(index, 1);
      }
    };
  }, []);
  return value;
};
export {
  CookbookProvider,
  useDirection,
  useThemeMode
};
//# sourceMappingURL=index.js.map
